import {ChangeDetectionStrategy, Component, OnDestroy, ViewChild} from '@angular/core';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    Observable,
    of,
    startWith,
    Subject,
    Subscription,
    switchMap,
    tap
} from "rxjs";
import {FormControl} from "@angular/forms";
import {LiveAnnouncer} from "@angular/cdk/a11y";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MemoryFormComponent} from "../shared/memory-form/memory-form.component";
import {AuthService} from "../_services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ContentService} from "../_services/content.service";
import {SnackbarService} from "../_services/snackbar.service";
import moment from 'moment';
import {MatTableDataSource} from "@angular/material/table";
import {catchError} from "rxjs/operators";
import {User} from "../_types/user";
import {Tag} from "../_types/content";
import {DraftPeriodUpload, MinimalMemory, ReducedTimeline, TimelineMemoriesResponse} from "../_types/memorybox";
import {UploadSessionInfo} from "../_types/admin";


export interface TimelineYear {
    year: number;
    memories: MinimalMemory[]
}


@Component({
    selector: 'app-timeline',
    templateUrl: './timeline.component.html',
    styleUrl: './timeline.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimelineComponent implements OnDestroy {
    displayedColumns: string[] = ['img', 'name', 'creator', 'date'];
    draftDisplayedColumns: string[] = ['img', 'name', 'date'];
    memoryTableSource: MatTableDataSource<MinimalMemory> = new MatTableDataSource();
    draftTableSource: MatTableDataSource<DraftPeriodUpload> = new MatTableDataSource();
    loading: boolean = true;
    paginationLoading: boolean = false;
    dateTimer: ReturnType<typeof setTimeout> | undefined;
    searchTerm: string | undefined;
    @ViewChild('tagInput') tagInput: any;
    filteredTags!: Observable<Tag[]>;
    tags: Tag[] = [];
    selectedTags: number[] = [];
    tagSearchControl: FormControl = new FormControl();
    permission: string | null = null;
    user!: User;
    id: number = 0;
    years: TimelineYear[] = []
    timeline: ReducedTimeline | undefined;
    order: string = 'desc';
    startDateField: FormControl = new FormControl();
    endDateField: FormControl = new FormControl();
    startDate: string | undefined;
    endDate: string | undefined;
    favouritesThumbnail: string = '';
    minDate: Date = new Date("1800-01-01");
    maxDate: Date = new Date();
    uploadSession: UploadSessionInfo | undefined;
    display: 'grid' | 'list' = 'grid';
    isRaa: boolean = false;
    isNextPageAllowed: boolean = true;
    page: number = 0;
    count: number = 0;
    limit: number = 16;
    drafts: DraftPeriodUpload[] = [];
    searchResults: MinimalMemory[] = [];

    private searchTermSubject: Subject<string> = new Subject<string>();
    private readonly searchSubscription: Subscription;

    constructor(public dialog: MatDialog,
                private route: ActivatedRoute,
                private authService: AuthService,
                private contentService: ContentService,
                private snackbarService: SnackbarService,
                private liveAnnouncer: LiveAnnouncer,
                private router: Router) {
        this.maxDate.setFullYear(this.maxDate.getFullYear() + 100);

        this.filteredTags = this.tagSearchControl.valueChanges
            .pipe(
                startWith(''),
                map(value => this._filterTags(value))
            );
        this.route.params.subscribe({
            next: params => {
                this.id = params['id'];
                this.authService.getUser().subscribe({
                    next: r => {
                        this.user = r;
                        this.authService.getDynamicSettings().then((r): void => {
                            this.isRaa = r.is_raa;
                            if (params['id'] && this.isRaa) {
                                this.getDrafts();
                            }
                            if (this.isRaa && this.user?.role.raa_is_limited && params['id']) {
                                this.contentService.getUploadSessionInfo(params['id']).subscribe({
                                    next: r => {
                                        this.uploadSession = r;
                                    }, error: error => {
                                        if (error != 'No UserUploadFrequencyLimit matches the given query.') {
                                            this.snackbarService.openSnackBar(error, 'error')
                                        }
                                    }
                                });
                            }
                        });
                    }
                })

                if (this.id) {
                    this.contentService.viewTimeline(this.id).subscribe({
                        next: r => {
                            this.timeline = r;
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error');
                            console.error(error);
                            this.loading = false;
                            this.router.navigate(['/timelines']);
                        }
                    })
                    this.getTimelineMemories();
                    this.getTimelinePermissions()
                } else if (this.user?.role?.role_type == 'young_person') {
                    // If no id get users timeline
                    this.contentService.viewMyTimeline().subscribe({
                        next: r => {
                            this.id = r.id;
                            this.getTimelinePermissions()
                            this.getTimelineMemories();
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                            this.loading = false;
                        }
                    });
                } else {
                    // Adults do not have a timeline
                    this.router.navigate(['/people']);
                }
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });


        this.contentService.getAllTags().subscribe({
            next: r => {
                this.tags = r.results;
            }, error: error => {
                console.error(error)
            }
        });

        this.searchSubscription = this.searchTermSubject.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            tap((): void => {
                this.loading = true;
            }),
            switchMap(value => {
                this.searchResults = [];
                let tags: string[] = [];
                for (let tag of this.tags) {
                    if (this.selectedTags.includes(tag.id)) {
                        tags.push(tag.name)
                    }
                }
                this.page = 0;
                this.count = 0;
                return this.contentService.getTimelineMemories(this.id, this.page, 10000, this.searchTerm, this.order, this.startDate, this.endDate, tags).pipe(
                    catchError(e => {
                        this.loading = false;
                        this.snackbarService.openSnackBar(e, 'error');
                        return of(null);
                    })
                );
            })
        ).subscribe({
            next: (r): void => {
                if (!r || !this.user) {
                    this.loading = false;
                    this.snackbarService.openSnackBar('There was an error please try again', 'error');
                    return
                }
                if (r.count === 0) {
                    this.snackbarService.openSnackBar('No albums match your filters', 'error');
                    this.liveAnnouncer.announce('No albums match your filters').then();
                }
                this.count = r.count;
                if (this.searchTerm) {
                    this.searchResults = r.results;
                } else {
                    this.setYearData(r)
                }
                this.setTableData(r.results);
                this.liveAnnouncer.announce(`Showing ${r.count} ${r.count === 1 ? 'album' : 'albums'}`).then();
            },
            error: (e): void => {
                this.searchResults = []
                this.memoryTableSource = new MatTableDataSource()
                this.loading = false;
                this.snackbarService.openSnackBar(e, 'error');
            }
        });
    }


    getTimelinePermissions(): void {
        this.contentService.viewTimelinePermissions(this.id).subscribe({
            next: r => {
                for (let permission of r) {
                    if (permission.user.id == this.user?.id) {
                        this.permission = permission.permission
                    }
                }

                if (this.permission !== 'Add but not View or Comment' && !this.isRaa) {
                    this.contentService.getFavouriteMedia(0, 1, this.timeline?.owner?.id, 'Image').subscribe({
                        next: res => {
                            if (res.results[0]?.media.thumbnail) {
                                this.favouritesThumbnail = res.results[0]?.media.thumbnail;
                            } else {
                                this.favouritesThumbnail = 'assets/images/yella.svg';
                            }
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            this.favouritesThumbnail = 'assets/images/yella.svg';
                            console.error(error)
                        }
                    });
                }
                if ((!this.permission || this.permission == 'No permission') && this.user?.role?.role_type !== 'admin') {
                    this.snackbarService.openSnackBar('Sorry you do not have permission to view this timeline', 'error');
                    this.router.navigate(['/noAccess']);
                    this.loading = false;
                    return;
                } else if (this.user?.role?.role_type == 'admin') {
                    this.permission = 'View, Comment and Add';
                }
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
                this.router.navigate(['/noAccess']);

            }
        });
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    navigate(url: string, id?: number, timelineId?: number): void {
        if (timelineId) {
            this.router.navigate([url, id, timelineId]);
        } else if (id) {
            this.router.navigate([url, id]);

        } else {
            this.router.navigate([url]);
        }
    }

    getDrafts(): void {
        this.contentService.getTimelineDrafts(this.id).subscribe({
            next: (drafts: DraftPeriodUpload[]): void => {
                this.drafts = drafts;
                this.draftTableSource = new MatTableDataSource(drafts);
            },
            error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    dateChange(event?: any, endDate?: boolean): void {
        this.page = 0;
        // only trigger filter if the end date has been inputted
        // or if the start date was inputted 2 second ago without the end being input
        // To reduce api calls

        if (event.value) {
            if (endDate) {
                let date: Date = new Date(event.value)
                this.endDate = date.toLocaleDateString('en-CA').slice(0, 10);
                clearTimeout(this.dateTimer);
                if (this.endDate && this.startDate) {
                    this.getTimelineMemories()
                }
            } else {
                let date: Date = new Date(event.value)
                this.startDate = date.toLocaleDateString('en-CA').slice(0, 10);
                const that: this = this;
                this.dateTimer = setTimeout((): void => {
                    if (this.endDate && this.startDate) {
                        that.getTimelineMemories();
                    }
                }, 2000);
            }
        }
    }

    clearDate(): void {
        this.page = 0;
        this.endDate = undefined;
        this.startDate = undefined;
        this.getTimelineMemories();
        this.startDateField.setValue(null);
        this.endDateField.setValue(null);
    }

    getTimelineMemories(order?: string): void {
        if (order) {
            this.order = order;
        }
        this.loading = true;
        let tags: string[] = []
        for (let tag of this.tags) {
            if (this.selectedTags.includes(tag.id)) {
                tags.push(tag.name)
            }
        }
        this.contentService.getTimelineMemories(this.id, this.page, this.limit, this.searchTerm, this.order, this.startDate, this.endDate, tags).subscribe({
            next: r => {
                this.isNextPageAllowed = !!r.next;
                this.setYearData(r)
                this.setTableData(r.results)
                this.loading = false;
                this.paginationLoading = false;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
                this.paginationLoading = false;
            }
        });


        this.authService.getDynamicSettings().then((r): void => {
            if (r.is_raa && this.user?.role.raa_is_limited && this.id) {
                this.contentService.getUploadSessionInfo(this.id).subscribe({
                    next: r => {
                        this.uploadSession = r;
                    }, error: error => {
                        if (error != 'No UserUploadFrequencyLimit matches the given query.') {
                            this.snackbarService.openSnackBar(error, 'error')
                        }
                    }
                });

            }
        });
    }

    setYearData(timelineMemoriesResponse: TimelineMemoriesResponse): void {
        if (this.page == 0) {
            this.years = [];
            if (timelineMemoriesResponse.max_year_for_timeline) {
                this.maxDate = new Date(timelineMemoriesResponse.max_year_for_timeline + '-12-31')
            }
            if (timelineMemoriesResponse.min_year_for_timeline) {
                this.minDate = new Date(timelineMemoriesResponse.min_year_for_timeline + '-01-01')
            }
        }
        this.count = timelineMemoriesResponse.count;
        for (let memory of timelineMemoriesResponse.results) {
            let year: number = 0
            if (memory.date) {
                year = moment(memory.date).year();
            }
            let indexOfYear: number = this.years.findIndex((x: TimelineYear): boolean => x.year === year)
            if (indexOfYear > -1) {
                this.years[indexOfYear].memories.push(memory);
            } else {
                this.years.push({
                    year: year,
                    memories: [memory]
                })
            }
        }
    }

    setTableData(memories: MinimalMemory[]): void {
        if (this.page > 0) {
            this.memoryTableSource.data = this.memoryTableSource.data.concat(memories);
            this.loading = false;
        } else {
            let data: MinimalMemory[] = [];
            if (this.permission !== 'Add but not View or Comment' && !this.isRaa) {
                data.push({
                    title: 'Favourite Memories',
                    cover_image_thumbnail_url: 'assets/images/vmb.png',
                    type: 'favourite',
                    id: 0
                });
            }
            if (this.user?.role?.role_type == 'admin') {
                data.push({
                    title: 'Hidden Media',
                    cover_image_thumbnail_url: 'assets/images/yella.svg',
                    type: 'hidden',
                    id: 0
                })
            }
            data = data.concat(memories)

            this.memoryTableSource = new MatTableDataSource(data)
            this.loading = false;
        }
    }


    selectTag(event: number): void {
        this.page = 0;
        const tag = event as any;
        this.selectedTags.push(tag);
        if (this.tagInput && this.tagInput.nativeElement) {
            this.tagInput.nativeElement.value = '';
        }
        this.getTimelineMemories();
    }

    removeTag(tag: number): void {
        this.page = 0;
        const i: number = this.selectedTags.findIndex((x: number): boolean => x === tag);
        if (i != null && i >= 0) {
            this.selectedTags.splice(i, 1);
        }
        this.getTimelineMemories();
    }

    checkIfInArray(tag: number, otherArray: number[]): boolean {
        const i: number = otherArray.findIndex((x: number): boolean => x === tag);
        return (i != null && i >= 0);
    }

    keyup(): void {
        if (this.searchTerm != null) {
            this.searchTermSubject.next(this.searchTerm);
        } else {
            this.searchTermSubject.next('');
        }
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchTermSubject.next(this.searchTerm);
        this.searchResults = [];
    }

    newMemory(): void {
        if (this.dialog.openDialogs.length > 0) {
            // Close all currently open dialogs
            this.dialog.openDialogs.forEach((dialogRef: MatDialogRef<any>): void => {
                dialogRef.close();
            });
        }
        window.scrollTo(0, 0);
        // stops google places location pop up from being in the wrong place
        const dialogRef: MatDialogRef<MemoryFormComponent> = this.dialog.open(MemoryFormComponent, {
            data: {timeline: this.timeline?.id},
            panelClass: 'fullWidthWindow',
        });
        dialogRef.afterClosed().subscribe((): void => {
            this.getTimelineMemories();
        });
    }

    exportTimeline(): void {
        this.contentService.requestExport(this.id).subscribe({
            next: r => {
                this.snackbarService.openSnackBar(r.message, 'success')
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    ExportOwnTimeline(): void {
        this.contentService.requestMemoryBoxDownload().subscribe({
            next: r => {
                this.snackbarService.openSnackBar(r.message, 'success')
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    newMediaForAPeriod(draft?: DraftPeriodUpload): void {
        if (this.dialog.openDialogs.length == 0) {
            import('../shared/upload-media-for-period/upload-media-for-period.component')
                .then(({UploadMediaForPeriodComponent}): void => {
                    const dialogRef: MatDialogRef<any> = this.dialog.open(UploadMediaForPeriodComponent, {
                        disableClose: true,
                        data: {
                            timeline: this.timeline?.id,
                            uploadSession: this.uploadSession,
                            unlimitedUploadForPeriod: this.isAbleToRaaUpload(),
                            draft
                        },
                        panelClass: 'fullWidthWindow',
                    });
                    dialogRef.afterClosed().subscribe((resp): void => {
                        if (resp) {
                            this.getDrafts();
                            this.getTimelineMemories();
                        }
                    });
                })
                .catch((error: unknown): void => {
                    console.error('Upload dialog failed to load', error);
                    this.snackbarService.openSnackBar('A new version of Virtual Memory Box is available. Refreshing now so you can retry.', 'error');
                    setTimeout((): void => window.location.reload(), 1500);
                });
        }
    }

    showRaaUploadForPeriod(): boolean {
        return (this.isAbleToRaaUpload() || !!(this.user?.role?.raa_is_limited && this.uploadSession)) && this.user?.role.role_type !== "young_person";
    }

    getDraftTooltip(): string {
        if (this.isAbleToRaaUpload()) return '';
        if (!this.uploadSession) return 'No upload session found';
        if (!this.uploadSession.can_upload) {
            return this.getUsedUploadMessage();
        }
        return this.getCanUploadMessage();
    }

    disableRaaUploadForPeriod(): boolean {
        return !!(this.user?.role?.raa_is_limited && !this.uploadSession?.can_upload);
    }

    getRaaUploadForPeriodTooltip(): string {
        if (!this.disableRaaUploadForPeriod()) {
            return this.uploadSession?.can_upload
                ? this.getCanUploadMessage()
                : 'New upload';
        }

        return this.uploadSession
            ? this.getUsedUploadMessage()
            : 'No upload session found';
    }

    getListImage(memory: MinimalMemory): string {
        if (memory.type == 'favourite') {
            return this.favouritesThumbnail || 'assets/images/vmb.png';
        } else if (memory.type == 'hidden') {
            return 'assets/images/yella.svg';
        } else {
            return memory.cover_image_thumbnail_url || 'assets/images/vmb.png';
        }
    }

    getListCreator(memory: MinimalMemory): string {
        return memory.creator ? this.authService.getDisplayName(memory.creator, undefined, undefined, 'timeline') : '';
    }

    openListItem(memory: MinimalMemory): void {
        if (memory.type == 'favourite') {
            if (this.timeline?.owner?.id == this.user.id) {
                this.navigate('/favourites');
            } else if (this.timeline?.owner?.id) {
                this.navigate('/favourites', this.timeline.owner?.id);
            }
        } else if (memory.type == 'hidden') {
            if (this.timeline?.id) {
                this.navigate('/hiddenMedias', this.timeline.id);
            }
        } else {
            this.navigate('/memory', memory.id);
        }
    }

    getListRouterLink(memory: MinimalMemory): (string | number)[] | null {
        if (memory.type == 'favourite') {
            if (this.timeline?.owner?.id == this.user.id) {
                return ['/favourites'];
            }
            return this.timeline?.owner?.id ? ['/favourites', this.timeline.owner?.id] : null;
        } else if (memory.type == 'hidden') {
            return this.timeline?.owner?.id && this.timeline?.id
                ? ['/hiddenMedia', this.timeline.owner?.id, this.timeline.id]
                : null;
        } else {
            return ['/memory', memory.id];
        }
    }

    parseDate(date: string | undefined, from: string, to: string): string {
        if (!date) return '';
        return moment(date, from).format(to)
    }

    triggerInfiniteScroll(): void {
        if (!this.isNextPageAllowed || this.loading || this.paginationLoading) return;
        this.paginationLoading = true;
        this.isNextPageAllowed = false;
        this.page += 1;
        this.getTimelineMemories();
    }

    protected canShowDrafts(): boolean {
        const hasAddPermissions = this.permission == 'Add but not View or Comment' || this.permission == 'View, Comment and Add';
        const isDraftEligible = this.user?.role.raa_is_limited || this.isAbleToRaaUpload();
        return !!(this.timeline?.id && this.isRaa && isDraftEligible && hasAddPermissions);
    }

    private isAbleToRaaUpload(): boolean {
        return this.isRaa && (!this.user?.role?.raa_is_limited || (this.user?.role?.raa_is_limited && !!this.uploadSession));
    }

    private getCanUploadMessage(): string {
        return 'You can create your upload for this period until ' +
            this.parseDate(this.uploadSession?.next_reset, 'YYYY-MM-DD', 'DD/MM/YYYY');
    }

    private getUsedUploadMessage(): string {
        return 'You have used your upload for this period you can create a draft or wait for your next upload on ' +
            this.parseDate(this.uploadSession?.next_reset, 'YYYY-MM-DD', 'DD/MM/YYYY');
    }

    private _filterTags(value: string): Tag[] {
        let filterValue: string = '';
        if (typeof value == 'string') {
            filterValue = value.toLowerCase();
        }
        let results: Tag[] = this.tags.filter((option: Tag): boolean => option.name.toLowerCase().includes(filterValue));
        results = results.filter((item: Tag, index: number): boolean => {
            return (results.indexOf(item) == index);
        });
        return results;
    }


}
